export const program = [
  {
    story_title: "Change as a Form of Death",
    story_body: (
      <div>
        One of the most powerful lessons I learned in my education and training
        is the idea that change, in often small, but nonetheless important ways
        is a kind of death. Moving to a new house, or town, or school, causes a
        person to recognize that things are not as they once were.
        <br />
        <br />
        This is true even when the change is potentially a good thing.
        <br />
        <br />
        Researchers at Stanford looked at how much people would save for a
        future self depending on their appearance and future traits. People
        saved significantly less for future selves unlike them. This makes
        sense, because when you change, you are a different person, a stranger.
        As such that current self is dead in a meaningful way. That’s part of
        the challenge of changing behaviors, you are killing a self in some
        sense and creating a stranger.
      </div>
    ),
    questions: [
      "Answer one or some of the questions—Did you look at your device? What was your change in usage? Did the decrease in usage change your mood?",
      "When have you been able/not able to identify with your future self? How can you better be able to recognize change in your life?",
    ],
  },
  {
    story_title: "Cues, Habits, and Free Will",
    story_body: (
      <div>
        The idea of addiction has not been an easy one for economists to deal
        with. In a world where people pursue their own best interests, why would
        they continue to do something that they feel is bad for them, and then
        spend money to check themselves into detox programs?
        <br />
        <br />
        Eventually, the idea of negative utility came into the picture, where
        using something yesterday could cause a withdrawal today, so that your
        use of a drug, a drink, etc. was not so much to feel good, but rather to
        stop feeling bad. While that helped explain things a bit more, there was
        still the question of why people would sometimes go from not consuming
        an addictive thing at all to binging on something almost immediately. In
        those situations, people seem to be acting against their own free will.
        <br />
        <br />
        Last decade, two researchers came up with a model based upon
        neuroscience and psychology research. They noted that people can be put
        in situations where the sight, sound or smell of something can activate
        their arousal to the point that they cannot control themselves in the
        moment. Think of it as a “hot state” at which point they no longer have
        a choice. Their takeaway, which matches those of researchers who work
        with addicts as well as those in other fields, was that the key time for
        decision-making was when people were in a “cold state.” A time when they
        were not around the source of their addiction, when their will power
        would be strongest.
        <br />
        <br />
        The point, in my opinion, is that when you are looking to reduce your
        tethering to technology, one of the key things is to not only seek to
        try and remove cues and triggers— whether they be the presence of a cell
        phone within reach or the presence of a specific app located on your
        device—but also to recognize that in the presence of a cue or trigger,
        you shouldn’t expect more from your willpower in those situations. Hot
        states can happen, and if you can recognize them when they are
        happening, then you have been given an important insight.
      </div>
    ),
    questions: [
      "Think of a time when you found it really hard not to check your phone? Where were you? Why do you think that happened?",
    ],
  },
  {
    story_title: "What is Bandwidth?",
    story_body: (
      <div>
        It’s a term that is now ubiquitous in some circles, and certainly more
        common generally than it was before. It conjures up images of Wifi
        routers and bars illuminated on cell phone screens. It is meant to
        describe the attentional capacity of us to engage on a task while
        (presumably) engaged in other ones at the same time. It parallels our
        conception of the cyber highway, ever widening, allowing for more and
        more lines of informational traffic to traverse it.
        <br />
        <br />
        The truth is, however, that our minds are not highways. With each
        additional subroutine in our organic RAM there are non-linear decreases
        in efficiency. Sans jargon, it means our “bandwidth” is a one lane
        highway that was never intended for lots of heavy traffic at one time.
        <br />
        <br />
        That does not mean we can’t nor shouldn’t “Think Fast”, as Daniel
        Kahneman described eloquently in his book on human psychology. Nor does
        it mean we shouldn’t be multi-tasking at times when it is required. Our
        minds evolved to perform these tasks but not solely in that fashion.
      </div>
    ),
    questions: [
      "How many things are you juggling in your mind right now?",
      "How many are truly necessary?",
    ],
  },
  {
    story_title: "A Menu of Options for Day 7",
    story_body: (
      <div>
        No matter how little you might be recapturing in terms of your time and
        bandwidth, you can use it to do something you had listed you would like
        to do with it. Pick out an activity or a couple of things that you could
        do on Day 7. Even if you didn’t, you can still imagine a few things you
        could do away from your devices. Considering options today gives you
        time to plan for what could be a phone call to an old friend, or doing
        an outside activity.
      </div>
    ),
    questions: [
      "Did your usage decrease?",
      "What would you like to do on Day 7? Specifically, what are two ways you want to redirect your bandwidth?",
      "What do you need to do? How will you plan for it?",
    ],
  },
  {
    story_title:
      "Okazaki Fragments and the Concept of Building our Knowledge Inefficiently",
    story_body: (
      <div>
        Human DNA is replicated through one of two mechanisms. The first
        mechanism follows a “leading strand” as it is built, brick by brick, of
        nucleic acids, a folding tower growing in relative order. The other side
        of the DNA follows a more chaotic approach, where pieces of RNA, known
        as Okazaki fragments, are placed here and there with no apparent rhyme
        or reason until that strand as well has a partner.
        <br />
        <br />
        When we are tethered to technology and follow platforms like Twitter and
        Instagram, Facebook and TikTok, there is no leading strand of knowledge
        built. Instead we are hit with a cacophony of message from politicians
        and athletes, superstars and friends. We eventually expand our knowledge
        but in a way that requires our precious attention to jump from one piece
        of information in one area to another, constantly shifting our attention
        to the point that we are adapted towards the hyperkinetic shifts but
        unable to read long-form articles with the same concentration.
      </div>
    ),
    questions: [
      "If you spent less time on your device, how did you use this extra time?",
      "Do you experience problems focusing on your day-to-day tasks? What is causing this distraction?",
    ],
  },
  {
    story_title: "Variable Rewards and Your RAM",
    story_body: (
      <div>
        When you are set to meet or call a friend at a given time, you may look
        forward to it, but it isn’t something that is usually at the top of your
        mind. It’s a nice thought, tucked away, because you know that you will
        likely enjoy the pleasure of someone else’s company at the moment. But
        what about when you are waiting to hear back from someone after emailing
        them? Or maybe you are waiting to see if anyone notices your IG post? If
        you are really excited to have that communication, the thought of
        checking back ever more frequently for any response on the platform,
        usually within reach of your fingertips can use more and more of your
        brain’s attention. Because you don’t know exactly when that might be,
        you continue to check back because in every 10 seconds that passes, it
        might have happened. This scenario, known as a variable reward schedule,
        was first coined by the psychologist B.F. Skinner and relates to a kind
        of behavior seen in a lot of animals including homo sapiens.
        <br />
        <br />
        This kind of basic reward mechanism, whether it was developed by
        accident or by design, is how most social media platforms operate. Since
        they are all on devices that are usually right next to us, we don’t have
        any barriers to checking back over and over again. The question is, over
        time, does it add anything to a day that could otherwise involve
        checking the same sites only once or twice, scheduled like that call
        with your friend?
      </div>
    ),
    questions: [
      "Are there any apps or sites that you check a lot, randomly, throughout the day?",
      "What would the upside and downside be to scheduling check-ins?",
    ],
  },
  {
    story_title: "Your Activity Day",
    story_body: (
      <div>
        Today was a day where you hopefully were able to do something different
        with some of your time that was usually spent on technology. There are
        various ways to be "productive" and redirect your bandwidth. We hope
        that it was helpful in giving you a different set of things to think
        about.
      </div>
    ),
    questions: [
      "With your saved time, did you do something different? What was it?",
      "After doing it, what were your thoughts? How do you feel?",
    ],
  },
  {
    story_title: "The Orbicularis Oculi Test",
    story_body: (
      <div>
        “That’s a fake smile.”
        <br />
        <br />
        We’ve all said that to ourselves. But how do we know this? How do we
        recognize that the upturning of our lips and a widening of our eyelids
        is not always reflecting an internal state of some sort of happiness.
        <br />
        <br />
        As it turns out, there is a little muscle that is on both sides of your
        eyes known as the Orbicularis Oculi. It contracts the skins around our
        eyes and others to see that we are exhibiting a “real” smile. It is not
        unreasonable to speculate that such differentiation has helped us evolve
        as social creatures.
        <br />
        <br />
        But what happens when the extent of our window to others’ moods is
        limited to short bursts of text and images that capture a fraction of a
        person’s emotional state? In those cases, we dispense with the tools we
        were passed on to us evolutionarily, relying instead like eagles in the
        darkness, where our emotional vision is rendered barely functional. It
        is useful to remember when we seek to immediately make assumptions about
        the entirety of a person’s emotional state and/or intention when seeing
        them online.
      </div>
    ),
    questions: [
      "Has a message you sent over social media ever been misinterpreted? Why was that?",
    ],
  },
  {
    story_title: "Addressed to Occupant",
    story_body: (
      <div>
        When I was in training, my team was tasked with discharging a patient
        who had been very difficult on the floor. Upon my attempts to go over
        instructions for his medications and follow-up appointments when he got
        home, the gentleman railed at me for close to 30 minutes about how his
        care was inadequate and that I was a terrible (student) doctor. While
        this criticism was unfounded in my opinion, I still felt terrible.
        <br />
        <br />A wise, senior, attending pulled me aside and said the following
        words to me: “Addressed to Occupant”
        <br />
        <br />I had no idea what he had meant.
        <br />
        <br />
        When I asked him for what he meant, he replied that when there is a
        disconnect between the level of anger directed to you and the potential
        explanation(s) for why they are angry with you, whatever they are
        expressing is an internal emotional state, meant to be released anywhere
        and everywhere possible at that moment. It does not matter who the
        person is on the receiving end, for it is not about them as individuals.
        It is only because they are there.
        <br />
        <br />
        This kind of behavior is worsened in cyberspace, which is designed to
        arouse us without context, connect without consideration. We are at the
        receiving end of those who are addressing to occupants everywhere,
        leading to further cascades of affect that are usually not helpful.
      </div>
    ),
    questions: [
      "When has someone chewed you out for no reason? Has it happened on-line?",
    ],
  },
  {
    story_title: "Anger vs. Disgust in Relationships",
    story_body: (
      <div>
        What is a more worrisome emotion for our societal fabric, anger or
        disgust? At first glance anger might be ones guess, for anger suggests
        an affective kindling for violence, inside the home and out in public.
        There is no doubt that anger can evolve into these kinds of danger.
        <br />
        <br />
        Disgust should not be dismissed out of hand either, though. Ask most
        marriage counselors what most worries them as a sign of a failing
        marriage and many would point to disgust. While anger is a negative
        emotion, it is still a closer relative of love (and connection) than
        disgust, which evokes a disconnect, a lack of recognition of the
        humanity of the other. It is essentially a lack of caring and trust that
        can easily calcify over time.
        <br />
        <br />
        When we hear about the “anger” on various sides of political and
        cultural divides, we might want to consider the fact that these kinds of
        emotions are universal in many ways and that it is possible that they
        represent a sub-conscious form of communication that represent
        experiences and worries that are only looking for validation, and that
        disgust or eye-rolling at this particular emotional content rather than
        the actions and words which, while requiring judgement, can be polluted
        as they are translated through biases, cultural norms and, yes,
        technology.
      </div>
    ),
    questions: [
      "Do you think that the anger you see in others that may disagree with you— politically, socially, etc.—is different or similar to your own?",
    ],
  },
  {
    story_title: "Activity Day",
    story_body: (
      <div>
        Today is another day that we slotted in time for you to think about what
        you want to do on day 14. Are you thinking of doing the same thing you
        did last week? Call or see an old friend? Write some campaign cards if
        it’s election season? Whatever it is we hope you are able to apply some
        of your time today to plan for it and time on day 14 to do it
      </div>
    ),
    questions: [
      "Reach out to a colleague, old friend or family member who holds views that you may disagree with?",
      'Pull out a sheet of paper, write down your "relationship" with your phone? What is it like right now? What do you hope for it to be like?',
    ],
  },
  {
    story_title: "The +1 Wheel",
    story_body: (
      <div>
        Most of you are likely familiar with the term 3rd wheel. When an extra
        person who knows someone comes along with that person on a date or
        pre-planned night out. Being a third wheel or more generally a “plus
        one” wheel isn’t necessarily a bad thing or a good thing, but it does
        change the dynamic with the others you might be out with.
        <br />
        <br />A cell phone or the social network embedded in the device you
        carry with you out can be a real plus one as well, and perhaps is
        something you have experienced; when your mind is not fully part of the
        conversation or fun because you have brought the plus one that only you
        know, the phone you hold in your palm or you see sitting on the table.
        Maybe your phone is not even in sight, but the thought(s) going through
        your mind are captured at least in part about what you want to share
        about your current socializing with the plus one, or maybe your mind is
        on the other things going on with the world of your plus one.
        <br />
        <br />
        This plus one whether it be good or bad, will fundamentally change the
        way in which you will experience the moment with the others you are
        there with in real life, and you won’t remember the moment as well, even
        if you take tons of pictures and video.
      </div>
    ),
    questions: [
      "Can you think of a time when you felt that your cell phone and/or the people on your social network were “with you” when socializing, or that you were more focused on what others were doing that you were connected to on social media?",
    ],
  },
  {
    story_title: "Positive and Normative Disagreements",
    story_body: (
      <div>
        One of the real challenges in society is the effective conduction of
        public disagreements. When done well, there can be a dialogue that
        leads, if not to compromise, at least an understanding of where the
        other side in a debate is coming from.
        <br />
        <br />
        One of the hardest things about this day and age is the advent of
        virtual public squares that seek to award arousal over thoughtfulness.
        In many of these places you can’t even be sure of who is arguing with
        you or their intentions.
        <br />
        <br />A very helpful technique when engaging in debates in any setting
        is to try and differentiate between the positive and normative types of
        arguments. Positive ones can be thought of as rational, where if X
        policy is pursued, Y will happen. Normative ones instead say that X
        should be pursued because Y is what should happen. For example, if
        thinking about our nation’s health care, a positive question might be
        what would the outcomes (cost and overall health) from a single payer
        system while the normative argument would be that single payer is the
        right thing to do irrespective of the cost because health care is a
        right and not a privilege.
        <br />
        <br />
        When arguments are broken down on those lines, it is possible to reach
        an agreement on the cause and effect of the positive aspects of a policy
        while also recognizing fundamentally different philosophies behind
        normative assumptions. It doesn’t mean disagreements end, but rather
        they can be broken down into parts that are manageable. Suffice it to
        say it is much harder to do in cyberspace.
      </div>
    ),
    questions: [
      "What is an issue that you have argued with someone else about. Can you break down the issue into cause and effect expected from given policies, why you believe a certain policy is the right one, and what positive and normative arguments support this?",
      "What is a political issue you feel passionately about? What might be a normative disagreement?",
    ],
  },
  {
    story_title: "Your Activity Day",
    story_body: (
      <div>
        Hope this second week has given you the space to do something different
        with some of your time that was previously spent on technology. While
        productivity spans a wide range based on your goals and values, we hope
        that this detox was helpful in giving you a different set of things to
        think about.
      </div>
    ),
    questions: [
      "Did you look at your device? What was your change in usage? Did the decrease in usage change your mood?",
      "With your saved time, did you do something different? What was it?",
      "After doing it, what were your thoughts? How do you feel?",
    ],
  },
  {
    story_title: "Rising Tides (of Stress) and Empathy",
    story_body: (
      <div>
        For those of you reading this under the shadow of the pandemic, there is
        a shared recognition of life not being “normal.” While no change ever
        affects people universally, there has likely never been (nor hopefully
        will ever be again) a shock to the system that has stressed so many,
        across so many walks of life.
        <br />
        <br />
        Do you remember the entry about “addressed to sender”? How people will
        not be nice for reasons that have nothing to do with the recipient of
        their ire? We are living in a time where so many—parents, children, the
        sick, the elderly, the front-line workers, the underserved and the
        ignored—all are suffering from the effects of the same thing. All the
        while, we have been limited to the tools and technologies that are at
        once lifelines to the world and decidedly imperfect tools to replace the
        human contact that most all of us need.
        <br />
        <br />
        There is a small, but meaningful, outcome from this pandemic, however,
        that relates to empathy. Empathy is best felt when the source of
        another’s pain is similar or even identical to your own. Given the
        universal nature of the pandemic and its effects, our anxieties are all
        boats rising on the same ocean of stress. Empathy is the greatest
        defense against the dangers of disconnection and disgust as they allow
        us to relate to another as a human being.
      </div>
    ),
    questions: [
      "How many things are you juggling in your mind right now?",
      "How many are truly necessary?",
    ],
  },
  {
    story_title: 'Experts" and "Elites"',
    story_body: (
      <div>
        I have a really good mechanic that I take my car to. His name is Gus and
        his knowledge of automobiles amazes me. He is the living embodiment of
        the kind of expertise people have written about in books like Sources of
        Power and Blink. He is able to, within minutes, diagnose my car’s
        problems and quickly come up with options for it. He is a master
        automotive clinician in every sense of the word.
        <br />
        <br />I remember talking with Gus one day about an experience he had
        when a client came into his shop and attempted to inform him exactly
        what is wrong with his car and when Gus patiently tried to explain that
        the real problem was likely different the other man quickly interrupted
        him with information that he had found on the internet.
        <br />
        <br />I mention this to illustrate the difference between “experts”, and
        “elites”. We are all experts. Whether it is in the practice of our
        chosen profession(s), the cooking of an old family recipe like the
        polenta dulce my grandmother used to cook for our family, or how to get
        around Boston without getting lost or stuck in traffic, we have through
        practice and learning, trial and error, developed skill sets that define
        us.
        <br />
        <br />
        How does it feel when someone comes in, often out of nowhere, to tell
        you that everything you have learned is wrong? When the person telling
        you this is clearly speaking from a position of minimal knowledge and
        experience? It is incredibly frustrating whether it relates to how one
        might best landscape a yard, treat a serious illness, or bake a cake.
        <br />
        <br />
        We are all experts, but the vast majority of us are not elites. Our
        expertise allows us to know when those who challenge us do so from a
        position of similar knowledge and experience, usually because they meet
        us from a position of respect- for they know how hard it is to learn the
        information and knowledge that is required. Remember this when listening
        to those who seek short-cuts to knowledge from cursory reads from the
        unknown internet, or when listening to talking heads for whom the domain
        of their knowledge never limits the range of their criticism. Their
        criticism of “experts” is no different than the ill- informed man at
        Gus’s shop, or the person who you may be imagining from your own life.
        Their challenge is about something else, and dangerous, because being
        experts on things is what helped humanity get to where it is today.
      </div>
    ),
    questions: [
      "What are you an expert on? What is it like to be challenged in that area by someone who is not?",
    ],
  },
  {
    story_title: "The Hidden Groups We're a Part Of",
    story_body: (
      <div>
        Have you ever talked with friends about the idea of technology being
        harmful? It is not uncommon in our experience to have people describe
        vastly different experiences using technology. For some, technology is
        only a good thing in their life. It makes them more productive, more
        connected, and they are able to disconnect from it on a moment’s notice.
        <br />
        <br />
        Other people, and perhaps this is you, think about devices and the apps
        within with a lot of ambivalence. You can relate to the good things
        those platforms offer, but simultaneously cannot disconnect easily.
        Technology can rule your attention and emotion, always at your top of
        mind.
        <br />
        <br />
        Why is that?
        <br />
        <br />
        While there can be many reasons, most have nothing to do with our
        character, or fortitude. An illustrative example comes from the world of
        genetics and obesity.
        <br />
        <br />
        In 2006, one of the first genetic variants linked with obesity was found
        in a large study in Massachusetts. The effect of the so-called “risk”
        variant and obesity was existent, but small at first glance. Later, it
        was found that if you split the population up by when they were born,
        the risk variant was either not linked with obesity at or (for those
        born after World War II) extremely linked. What it showed was that
        people, even siblings within the same family, could react very
        differently to changes in their environment based on whether they
        inherited one or more of those risk variants and when they were born.
        This findings are similar to many other studies looking at the risk of
        alcohol or drug addiction, namely that it’s easier for some people to
        control their behavior and others.
      </div>
    ),
    questions: [
      "Are you someone who can disconnect easily? How will you use this knowledge moving forward?",
    ],
  },
  {
    story_title: 'Being "Entertained"',
    story_body: (
      <div>
        Are we entertained by those individuals whom we love? I would suspect
        that most of you would easily answer “yes.” Perhaps it is an actor like
        Denzel Washington or Meryl Streep. A singer like Billie Eilish or
        Pharrell Williams. Even talk show hosts like Oprah or Howard Stern.
        People who come across in interviews as people who you “like,” even if
        you have never met them. These people are likable AND entertaining.
        <br />
        <br />
        The fact that likable and entertaining are separated is not by accident,
        for, in truth, people who we dislike, who either appear loathsome
        (and/or in fact are loathsome) based on their words and actions, can
        also be profoundly entertaining. The heels, whether they be professional
        wrestlers, reality TV stars, or musicians, all can be profoundly
        compelling. We “hate-watch” them because they are often interesting and
        reflect those less-ideal parts of ourselves that we all can relate to.
        <br />
        <br />
        Whatever the reasons, we often find ourselves attracted to
        following/watching/listening to those people because it is a form of
        entertainment for us. While in many cases that can be a healthy
        diversion in other cases it might not be. There are many, including
        those with whom you might very much agree with on any number of levels,
        whose career is based upon fomenting emotions that at once make them
        magnets for attention. The negative emotions they tap into make it that
        much harder to empathize with others, thus threatening the foundational
        ties with have with our friends, loved ones, and society at large.
        <br />
        <br />
        This is not to say that we should not ignore the words and actions of
        those whose actions and words we find distasteful, objectionable or even
        appalling. Rather, it means that we should not seek out those
        individuals, many of whom are only entertaining and neither enlightening
        or intelligent. There are people out there whom you can pay attention to
        who are thoughtful, diverse, talented, and interesting who are also
        entertaining. We list a few options on day 21 that fit into that
        category for us that we think you might enjoy as well.
      </div>
    ),
    questions: [
      "Are there any politicians or public figures who you hate watch/listen to? Why do you do it?",
      'Who do you "hate-watch"? Are there people you watch, and agree with, because they arouse and escalate our hate and emotional response to people in the question above?',
    ],
  },
  {
    story_title: "Playing White and the Pleasure of Pro-activity",
    story_body: (
      <div>
        One of the things about the devices, platforms, and content of our
        current age is that it is designed to make us react to media. After the
        initial swipe or login, it is designed to make us react to content and,
        presumably, continue to engage with it based on those reactions. Like
        playing chess with someone who always plays the white pieces, their move
        is the first one and you are reacting from the first move. Remember the
        entry on hot and cold states? This is very much what happens when the
        platform moves first. It seeks to gives those cues almost immediately.
        <br />
        <br />
        One of our goals at the Orpheus Pledge is to make people recognize what
        is happening when you engage with these technologies. This recognition
        at a minimum can give you a sense of control over what is happening and
        also perhaps tools to either restrict your engagement at a lower level
        or even to refrain from using the platforms; i.e. to play white yourself
        or not at all.
        <br />
        <br />
        There is a deep pleasure that we have felt when we are able to recognize
        these aspects of our behaviors and are able to make the first move by
        stopping our doom scrolling or even choosing not to scroll at all. It
        reflects the joy of having agency, or control over our those things
        around us.
      </div>
    ),
    questions: [
      "Has Orpheus made you more aware of your behavior when and after you use your device? If so, how?",
      "Do you feel more in control with technology now? Why or why not?",
    ],
  },
  {
    story_title: "The Only Real Change Possible",
    story_body: (
      <div>
        As you approach the end of the program, there is a natural tendency to
        balance hope versus fear. Whether the behavioral modifications sought
        from the outset might suggest a “new you” that has been reborn into a
        better person or, perhaps someone destined to return to a tethered
        relationship to technology that will continue to cycle between
        dependence and distance.
        <br />
        <br />
        Many programs that focus on chemical and other addictions such as
        Alcoholics Anonymous make it a tenet of their approach that the struggle
        to maintain a balance is a life-long one and a daily struggle that can
        vary in difficulty.
        <br />
        <br />
        This concept is valuable and important. It also reflects, on a deeper
        level, what change is. Change at its essence is not behavioral, rather
        it is informational. What this means is that the best we can hope from
        these sorts of approaches and experiences is that we gain more knowledge
        about ourselves, that we are given further tools to assess and analyze
        the choices we may make, ideally from a point of perspective that is at
        a distance from the on-the-ground/in-the-moment situations we oftentimes
        operate in. This knowledge, whether it be a recognition of the ongoing
        challenges to our attention and mood technological platforms will place
        upon us, or the ways in which we will vary our usage of technology over
        time, gives us a recognition and a sense of control, even when
        circumstance or other stressors might make us seem like we don’t have
        it.
      </div>
    ),
    questions: [
      "What are the things you learned about yourself in relation to technology during the program?",
    ],
  },
  {
    story_title: "Self Reflection",
    story_body: <div></div>,
    questions: [
      "First question, what do you want out of life?",
      "Where are you right now?",
      "How does impulsively or unconsciously checking your device or a social media app make you feel?",
      "Do you want to retain more control over your tech usage - what is your plan?",
      "When do your devices make you more anxious or stressed?",
      "What are 3 concrete steps you can take to minimize unhealthy tethering to your devices?",
    ],
  },
];
